/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { environment } from 'environment';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { map, Observable, switchMap } from 'rxjs';

/**
 * This service is responsible for securely loading assets (like images) from a given URL.
 * It sanitizes the URL for safe usage in the DOM and provides a fallback if loading fails
 * or the URL is empty.
 */
@Injectable({
	providedIn: 'root',
})
export class SecureAssetService {
	private http = inject(HttpClient);
	private sanitizer = inject(DomSanitizer);

	public loadAsset(url: string | Observable<string>): Observable<SafeUrl | null> {
		if (typeof url === 'string') {
			return this.getAsset$(this.generateFullUrl(url));
		}

		return url.pipe(
			switchMap((urlValue) => {
				return this.getAsset$(this.generateFullUrl(urlValue));
			})
		);
	}

	private generateFullUrl(url: string): string {
		if (url.startsWith('http')) {
			return url;
		}
		return `${environment.restApiUrl}/${url}`;
	}

	private getAsset$(url: string): Observable<SafeUrl | null> {
		return this.http.get(url, { responseType: 'blob' }).pipe(
			map((blob) => {
				return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
			})
		);
	}
}
